import {
  Center,
  Container,
  Group,
  LoadingOverlay,
  Stack,
  Tabs,
  Text,
} from "@mantine/core"
import { CertificateCategory } from "@tfg/db"
import { Loader } from "@tfg/ui"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"

import { ActivitiesProvider } from "~/components/contexts/useActivitiesContext"
import { HeaderControlsProvider } from "~/components/contexts/useHeaderControlsContext"
import { useGetCertificateRequestsForSupplierStats } from "~/gen/api"
import { useFindOperationChainsQuery } from "~/pages/Admin/Ingestion/OpChains/hooks/useFindOperationChainsQuery"
import { CertificateRequestsBatchModalProvider } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/certificateRequestsBatchModal/store/CertificateRequestsBatchModalContext"
import { TraceabilityRequestsList } from "~/pages/SupplierPortal/TraceabilityRequests/TraceabilityRequestsList.tsx"
import { CertificateRequests } from "~/pages/SupplierPortal/TraceabilityRequests/components/CertificateRequests.tsx"
import { ProdsiteRequests } from "~/pages/SupplierPortal/TraceabilityRequests/components/ProdsiteRequests.tsx"
import { useProductionSiteFieldRequestsQuery } from "~/pages/SupplierPortal/TraceabilityRequests/hooks"
import { useGetTraceabilityRequestsStats } from "~/pages/SupplierPortal/TraceabilityRequests/hooks/useGetTraceabilityRequestsStats"
import { getOpenAndClosedProdsiteFieldRequests } from "~/pages/SupplierPortal/TraceabilityRequests/utils"
import { useAuth } from "~/store/auth-context"

import { RequestTabHeader } from "./components/RequestTabHeader"

export const TraceabilityRequests = () => {
  const { t } = useTranslation(["traceability"])
  const { requestType } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: operationChainsResponse } = useFindOperationChainsQuery({})

  const {
    data: traceabilityStats,
    isPending: isTraceabilityStatsLoading,
    isError: isTraceabilityStatsError,
  } = useGetTraceabilityRequestsStats()

  const {
    data: certificateStats,
    isPending: isCertificateStatsLoading,
    isError: isCertificateStatsError,
  } = useGetCertificateRequestsForSupplierStats()

  const {
    data: productionSiteFieldRequests,
    isPending: isProductionSiteFieldRequestsLoading,
    isError: isProductionSiteFieldRequestsError,
  } = useProductionSiteFieldRequestsQuery()

  const isLoading =
    isTraceabilityStatsLoading ||
    isCertificateStatsLoading ||
    isProductionSiteFieldRequestsLoading
  const isError =
    isTraceabilityStatsError ||
    isCertificateStatsError ||
    isProductionSiteFieldRequestsError

  if (isError) {
    return (
      <Center h="100%">
        <Text c="red" size="xl">
          {t("request.errors.generic")}
        </Text>
      </Center>
    )
  }
  if (isLoading) {
    return <Loader fullPage />
  }

  const { openRequests: openProdsitefieldRequests } =
    getOpenAndClosedProdsiteFieldRequests(productionSiteFieldRequests, user?.id)

  return (
    <>
      <ActivitiesProvider>
        <Container
          fluid
          style={{
            height: "calc(100vh - var(--mantine-supplier-header-height))",
            overflow: "auto",
          }}
        >
          <LoadingOverlay visible={isLoading} />
          <Tabs
            value={requestType}
            onChange={(newRequestType) =>
              navigate(`/supplier/requests/${newRequestType}`)
            }
            defaultValue="traceability"
            px="var(--tfg-layout-padding-x)"
            py="var(--tfg-layout-padding-y)"
            w="100%"
          >
            <div
              style={{
                position: "sticky",
                zIndex: "100",
                top: 0,
              }}
            >
              <Tabs.List
                justify="space-between"
                bg="var(--mantine-color-supplier-header-bg)"
              >
                <Group>
                  <Tabs.Tab value="traceability">
                    <RequestTabHeader
                      count={traceabilityStats.opened}
                      label={t("requests.tabs.traceability")}
                    />
                  </Tabs.Tab>

                  <Tabs.Tab value="prodsitefield">
                    <RequestTabHeader
                      count={openProdsitefieldRequests.length}
                      label={t("requests.tabs.prodsitefield")}
                    />
                  </Tabs.Tab>
                  <Tabs.Tab value="certificatesScope">
                    <RequestTabHeader
                      count={certificateStats.data.scope.opened}
                      label={t("requests.tabs.certificates.scope")}
                    />
                  </Tabs.Tab>
                  <Tabs.Tab value="certificatesTransaction">
                    <RequestTabHeader
                      count={certificateStats.data.transaction.opened}
                      label={t("requests.tabs.certificates.transaction")}
                    />
                  </Tabs.Tab>
                </Group>
              </Tabs.List>
            </div>
            <Container fluid py="var(--tfg-layout-padding-y)">
              <Tabs.Panel value="prodsitefield">
                <Stack
                  p="md"
                  bg="var(--mantine-color-supplier-background)"
                  mih="calc(100vh - 114px)"
                >
                  {requestType === "prodsitefield" &&
                    (isProductionSiteFieldRequestsLoading ? (
                      <Center h="100%">
                        <Loader />
                      </Center>
                    ) : isProductionSiteFieldRequestsError ? (
                      <Text>{t("request.errors.generic")}</Text>
                    ) : (
                      <ProdsiteRequests
                        prodsiteRequests={productionSiteFieldRequests}
                      />
                    ))}
                </Stack>
              </Tabs.Panel>
              <Tabs.Panel value="traceability">
                <HeaderControlsProvider>
                  <TraceabilityRequestsList
                    operationChains={operationChainsResponse?.data ?? []}
                  />
                </HeaderControlsProvider>
              </Tabs.Panel>
              <Tabs.Panel value="certificatesScope">
                <Stack
                  p="md"
                  bg="var(--mantine-color-supplier-background)"
                  mih="calc(100vh - 114px)"
                >
                  <CertificateRequests category={CertificateCategory.SCOPE} />
                </Stack>
              </Tabs.Panel>
              <Tabs.Panel value="certificatesTransaction">
                <Stack
                  p="md"
                  bg="var(--mantine-color-supplier-background)"
                  mih="calc(100vh - 114px)"
                >
                  <CertificateRequestsBatchModalProvider>
                    <CertificateRequests
                      category={CertificateCategory.TRANSACTION}
                    />
                  </CertificateRequestsBatchModalProvider>
                </Stack>
              </Tabs.Panel>
            </Container>
          </Tabs>
        </Container>
      </ActivitiesProvider>
    </>
  )
}
